/**
 * Consecutive calendar-day streaks from daily activity buckets.
 */

import { addCalendarDaysToYmd } from "./notificationPeriod";

export type DailyStreakPoint = {
  date: string;
  trade_count: number;
  total_pnl: number;
};

export type TradingDayStreakMetrics = {
  currentDaysTradedStreak: number;
  longestDaysTradedStreak: number;
  currentWinningDaysStreak: number;
  longestWinningDaysStreak: number;
};

const EMPTY_METRICS: TradingDayStreakMetrics = {
  currentDaysTradedStreak: 0,
  longestDaysTradedStreak: 0,
  currentWinningDaysStreak: 0,
  longestWinningDaysStreak: 0,
};

function longestConsecutiveRun(sortedYmds: string[], timeZone: string): number {
  if (sortedYmds.length === 0) {
    return 0;
  }

  let longest = 1;
  let current = 1;

  for (let i = 1; i < sortedYmds.length; i++) {
    const prev = sortedYmds[i - 1];
    const next = sortedYmds[i];
    const expectedNext = addCalendarDaysToYmd(prev, 1, timeZone);
    if (next === expectedNext) {
      current += 1;
      longest = Math.max(longest, current);
    } else if (next !== prev) {
      current = 1;
    }
  }

  return longest;
}

function currentConsecutiveRun(sortedYmds: string[], timeZone: string): number {
  if (sortedYmds.length === 0) {
    return 0;
  }

  const traded = new Set(sortedYmds);
  let cursor = sortedYmds[sortedYmds.length - 1];
  let count = 0;

  while (traded.has(cursor)) {
    count += 1;
    cursor = addCalendarDaysToYmd(cursor, -1, timeZone);
  }

  return count;
}

function uniqueSortedYmds(ymds: string[]): string[] {
  return Array.from(new Set(ymds.filter((d) => /^\d{4}-\d{2}-\d{2}$/.test(d)))).sort();
}

export function calculateTradingDayStreaks(
  points: DailyStreakPoint[],
  timeZone: string
): TradingDayStreakMetrics {
  if (!points.length) {
    return EMPTY_METRICS;
  }

  const tradedDays = uniqueSortedYmds(
    points.filter((p) => p.trade_count > 0).map((p) => p.date.slice(0, 10))
  );

  const winningDays = uniqueSortedYmds(
    points
      .filter((p) => p.trade_count > 0 && p.total_pnl > 0)
      .map((p) => p.date.slice(0, 10))
  );

  return {
    currentDaysTradedStreak: currentConsecutiveRun(tradedDays, timeZone),
    longestDaysTradedStreak: longestConsecutiveRun(tradedDays, timeZone),
    currentWinningDaysStreak: currentConsecutiveRun(winningDays, timeZone),
    longestWinningDaysStreak: longestConsecutiveRun(winningDays, timeZone),
  };
}
